import React, {
  FC,
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
  Fragment,
} from "react";
import { RemoveScroll } from "react-remove-scroll";

import classNames from "classnames";
import tw from "twin.macro";

import {
  StyledComboBoxCloseSmallIcon,
  StyledComboBoxCollapseExpandSingleIcon,
  StyledComboBoxInput,
  StyledComboBoxList,
  StyledChipWrapper,
  StyledInfoIcon,
  StyledInfoSmallIcon,
} from "./Styles";
import { Portal } from "../../helpers";
import { ConditionalWrapper } from "../../helpers/ConditionWrapper/ConditionWrapper";
import { useIsFirstRender } from "../../hooks/useIsFirstRender";
import { useOnClickOutside } from "../../hooks/useOnClickOutside";
import useForkRef from "../../utils/useForkRef";
import { Autocomplete } from "../Autocomplete";
import {
  IAutocompleteProps,
  isOptionGroup,
} from "../Autocomplete/Autocomplete";
import { Avatar, AvatarProps } from "../Avatar";
import { Checkbox } from "../Checkbox";
import { Chip } from "../Chip";
import { IconButton } from "../IconButton";
import { InputAdornment } from "../Input/InputAdornment";
import { StyledInput, StyledInputWrapper } from "../Input/Styles";
import { InputMode, InputSize } from "../Input/types";
import { SpinnerLoader } from "../Loaders/Spinner/SpinnerLoader";
import { MultiAvatar } from "../MultiAvatar";
import { Popper } from "../Popper";
import { RadioButton } from "../RadioButton";
import { IOptionGroupProps, IOptionItemProps } from "../Select";
import { OptionContent } from "../Select/OptionContent/OptionContent";
import { Tooltip } from "../Tooltip";
import { Trigger } from "../Trigger";
import { Typography } from "../Typography/Typography";

const sizeMap = {
  small: 18,
  regular: 24,
  big: 30,
  biggest: 40,
};

export interface IComboBoxProps
  extends Omit<
    IAutocompleteProps,
    "handleChange" | "inputEl" | "clearInputOnSelect"
  > {
  /** Set combobox in to loading state */
  loading?: boolean;
  /** Text to display while in loading state */
  loadingText?: string;
  /** onChange callback */
  onChange?: (
    e: (string | number)[] | string | number | null | undefined
  ) => void;
  /** onInputChange callback */
  onInputChange?: (text: string) => void;
  /** Placeholder for Select input */
  placeholder?: string;
  /** disable Input element */
  disabled?: boolean;
  /** Combobox size */
  size?: InputSize;
  /** Invalid state */
  invalid?: boolean;
  /** Render Chip */
  renderChip?: <T extends IOptionItemProps>(
    item: T,
    key?: string | number
  ) => JSX.Element;
  /** Close menu on change */
  forceCloseMenu?: boolean;
  /** Limit chips */
  limitChips?: number;
  /** Hidden number text */
  hiddenNumberText?: (hidden: number) => string;
  /** Render Autocomplete in <Portal> */
  inPortal?: boolean;
  /** inputWrapperClassName */
  inputWrapperClassName?: string;
  /** scrollWrapper */
  scrollWrapper?: boolean;
  /** onClose (f) will be called when Popper closes */
  onClose?: () => void;
  /** onOpen (f) will be called when Popper opens */
  onOpen?: () => void;
  /** onClear (f) will be called when user clear input on X icon*/
  onClear?: () => void;
  onDeselect?: () => void;
  /** Open state */
  open?: boolean;
  /** Trigger mode */
  triggerMode?: InputMode;
  /** Hide clear button */
  hideClearButton?: boolean;
  /** Error message to display */
  errorMessage?: string;
}

export const ComboBox: FC<IComboBoxProps> = ({
  options = [],
  selected,
  loading = false,
  loadingText,
  onChange,
  onInputChange,
  placeholder,
  handleEmptyAction,
  disabled = false,
  size = "regular",
  invalid = false,
  type = "single",
  renderChip,
  forceCloseMenu = type === "single",
  renderOption,
  limitChips = 2,
  hiddenNumberText,
  inPortal = false,
  inputWrapperClassName,
  scrollWrapper,
  onClose,
  onClear,
  onDeselect,
  defaultValue,
  open: defaultOpen = false,
  onOpen,
  triggerMode,
  noResultText,
  emptyValue,
  disableVirtualization,
  hideClearButton = false,
  errorMessage,
  ...prop
}) => {
  const selectedName = useMemo(() => {
    let value = "";
    if (!selected) {
      return value;
    }
    options.map((v) => {
      if ((v as IOptionGroupProps)?.options) {
        return (v as IOptionGroupProps).options.find((q) => {
          if (q.id === selected) {
            value = q.name;
            return;
          }
        });
      } else {
        if (v.id === selected) {
          value = v.name;
          return;
        }
      }
    });
    return value;
  }, [options, selected]);
  const [value, setValue] = useState(selectedName);
  const [focused, setFocused] = useState<boolean>(false);
  const handleOnChange = useCallback(
    (e) => {
      setValue(e.target.value);
      onInputChange?.(e.target.value);
    },
    [onInputChange]
  );
  const childNode = useRef<HTMLInputElement | null>();
  const elementRef = useRef<HTMLInputElement | null>(null);
  const handleRef = useForkRef(childNode, elementRef);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const chipWrapper = useRef<HTMLDivElement>(null);
  const comboBoxRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(defaultOpen);
  const isFirstRender = useIsFirstRender();

  useOnClickOutside(wrapperRef, (e) => {
    if (e.target && comboBoxRef.current?.contains(e.target as Node)) {
      return;
    }
    setOpen(false);
  });

  const handleOnKeyDown = useCallback(
    (e) => {
      if (e.key === "Escape" && open) {
        setOpen(false);
        setValue(selectedName);
        e.stopPropagation();
      }

      if (e.key === "Enter" && type === "multiple") {
        setValue("");
      }

      if (e.key === "Enter" && !open && focused) {
        setOpen(true);
        onOpen?.();
      }
    },
    [focused, onOpen, open, selectedName, type]
  );

  useEffect(() => {
    open && childNode?.current?.focus();
    !open && childNode?.current?.blur();
  }, [childNode, open]);

  useEffect(() => {
    setValue(selectedName);
  }, [selectedName]);

  useEffect(() => {
    if (open !== defaultOpen) setOpen(defaultOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  useEffect(() => {
    if (!open && !isFirstRender && !focused) {
      if (typeof onClose === "function") {
        onClose();
      }
    }
  }, [focused, isFirstRender, onClose, open]);

  const emptyAction = useCallback(
    (v) => {
      handleEmptyAction?.(v);
      setOpen(false);
    },
    [handleEmptyAction]
  );

  const handleChange = useCallback(
    (selectedValue) => {
      if (selectedValue !== selected && typeof onChange === "function") {
        onChange(selectedValue);
      }
      if (forceCloseMenu) {
        setOpen(false);
      }
    },
    [forceCloseMenu, onChange, selected]
  );

  const handleOpen = useCallback(() => {
    if (!disabled) {
      setOpen(true);
      onOpen?.();
    }
  }, [disabled, onOpen]);

  const handleRenderOption = useCallback(
    (option, props) => {
      if (typeof renderOption === "function") {
        return renderOption(option, props);
      }
      if (type === "multiple") {
        return (
          <>
            <OptionContent
              imageUrl={option.image}
              color={option.color}
              textColor={option.textColor}
              name={option.name}
            />
            <Checkbox {...props} />
          </>
        );
      }
      return (
        <>
          <OptionContent
            imageUrl={option.image}
            color={option.color}
            textColor={option.textColor}
            name={option.name}
          />
          <RadioButton {...props} />
        </>
      );
    },
    [type, renderOption]
  );

  const handleDeselect = useCallback(
    (e) => {
      e.stopPropagation();
      if (typeof onDeselect === "function") {
        onDeselect();
      } else if (type === "multiple") {
        onChange?.([]);
      } else {
        onChange?.("");
      }

      onClear?.();
      setOpen(false);
    },
    [type, onChange, onClear, onDeselect]
  );

  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
  }, []);

  const handleBlur = useCallback(() => {
    setValue(selectedName);
    setFocused(!focused);
  }, [focused, selectedName]);

  const renderChipAdornment = useCallback(
    (option, index) => {
      if (typeof renderChip === "function") {
        return renderChip(option, index);
      }
      const avatarProps: Partial<AvatarProps> = {
        size: 18,
      };
      switch (size) {
        case "big":
          avatarProps.size = 22;
          break;
        case "small":
          avatarProps.size = 14;
          break;
      }
      const startAdornment = option.image ? (
        <Avatar
          css={`
            ${tw`tw-my-0.5`}
            ${tw`tw-mr-0.5`}
          `}
          url={option.image}
          {...avatarProps}
        />
      ) : undefined;

      const onClose = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (Array.isArray(selected) && selected.includes(option.id)) {
          if (typeof onChange === "function") {
            const newSelected = selected.filter((item) => item !== option.id);
            onChange(newSelected);
          }
        }
      };

      let showOnClose = true;
      if (disabled) {
        showOnClose = false;
      }
      if (option.color) {
        showOnClose = false;
      }

      return (
        <Chip
          className="c-combo-box-chip"
          startAdornment={startAdornment}
          label={option.name}
          key={index}
          backgroundColor={option.color}
          color={option.chipColor}
          onClose={showOnClose ? onClose : undefined}
          size={size}
        />
      );
    },
    [disabled, onChange, renderChip, selected, size]
  );

  const autoSize = useMemo(() => {
    if (
      type === "multiple" &&
      open &&
      Array.isArray(selected) &&
      selected.length > 1
    ) {
      return "auto";
    }
    return size;
  }, [open, selected, size, type]);

  const startAdornment = useMemo(() => {
    if (type === "multiple" && Array.isArray(selected) && selected.length > 0) {
      const elements: JSX.Element[] = [];
      let total = 0;
      options.forEach((option, index) => {
        const isGrouped = isOptionGroup(option);
        if (selected.includes(option.id) && !isGrouped) {
          total = total + 1;
          elements.push(renderChipAdornment(option, index));
        } else if (isGrouped) {
          const groupedOptions = option.options.filter((o) =>
            selected.includes(o.id)
          );
          total = total + groupedOptions.length;
          if (groupedOptions.length > 0) {
            groupedOptions.forEach((o, _index) => {
              elements.push(renderChipAdornment(o, `${index}_${_index}`));
            });
          }
        }
      });
      let hidden = 0;
      if (!open && limitChips > 0 && elements.length > limitChips) {
        hidden = elements.splice(
          limitChips,
          elements.length - limitChips
        ).length;
      }

      return (
        <Fragment>
          {elements}
          {hidden > 0 && (
            <Typography
              variant={
                size === "small"
                  ? "Caption 1"
                  : size === "regular"
                  ? "Body 2"
                  : "Body 1"
              }
              css={`
                display: flex;
                flex-shrink: 0;
                align-items: center;
              `}
            >
              {typeof hiddenNumberText === "function"
                ? hiddenNumberText(hidden)
                : `+${hidden}`}
            </Typography>
          )}
        </Fragment>
      );
    }
    if (type === "single" && selected) {
      let optionItem;
      options.forEach((option) => {
        if (isOptionGroup(option)) {
          optionItem = option.options.find((child) => child.id === selected);
        } else if (!optionItem && option.id === selected) {
          optionItem = option;
        }
      });
      if (optionItem && optionItem.image) {
        const avatarSize = size === "big" ? 22 : size === "regular" ? 18 : 14;
        if (Array.isArray(optionItem.image)) {
          return <MultiAvatar url={optionItem.image} size={avatarSize} />;
        }
        return <Avatar url={optionItem.image} size={avatarSize} />;
      }
    }

    return undefined;
  }, [
    type,
    selected,
    options,
    open,
    limitChips,
    hiddenNumberText,
    renderChipAdornment,
    size,
  ]);

  const showXIcon =
    Array.isArray(selected) &&
    type === "multiple" &&
    selected.length > 0 &&
    !hideClearButton;

  const hasSelected =
    type === "multiple" && Array.isArray(selected) && selected.length > 0;

  const showMenu = useMemo(() => {
    const hasOptions = options?.length > 0;

    const valueExists = (option: IOptionItemProps) =>
      option.id.toString().includes(value) || option.name.includes(value);

    const isValueInOptions = options?.some((option) =>
      isOptionGroup(option)
        ? option.options.some(valueExists)
        : valueExists(option)
    );

    return (
      (hasOptions && (!!defaultValue || !value)) ||
      (value && (isValueInOptions || emptyValue || noResultText))
    );
  }, [defaultValue, emptyValue, noResultText, options, value]);

  return (
    <div className="c-combo-box">
      {type === "multiple" ? (
        <>
          <StyledInputWrapper
            $disabled={disabled}
            $size={size}
            $invalid={invalid}
            ref={comboBoxRef}
            $mode={triggerMode}
            css={`
              display: flex;
              justify-content: space-between;
              flex: 1;
              height: ${autoSize === "auto" ? "auto" : undefined};
              overflow-y: ${autoSize === "auto" && scrollWrapper
                ? "scroll"
                : undefined};
            `}
            className={classNames(
              "c-combo-box-input-wrapper",
              inputWrapperClassName
            )}
            onClick={() => {
              elementRef.current?.click();
            }}
          >
            <StyledChipWrapper
              $autoSize={autoSize}
              $open={open}
              ref={chipWrapper}
              css={`
                max-height: ${autoSize === "auto" && scrollWrapper
                  ? sizeMap[size] * 3 + 8 + "px"
                  : undefined};
              `}
            >
              {startAdornment}
              <StyledInput
                className="c-combo-box-input"
                onBlur={handleBlur}
                onFocus={() => {
                  setFocused(!focused);
                }}
                onClick={handleOpen}
                ref={handleRef}
                value={loading && loadingText ? loadingText : value}
                onKeyDown={handleOnKeyDown}
                onChange={handleOnChange}
                placeholder={!hasSelected ? placeholder : undefined}
                $mode={triggerMode}
                disabled={disabled}
                $size={size}
                $loading={loading}
                style={{
                  display: "flex",
                  flex: 1,
                  alignSelf: "center",
                  width: autoSize === "auto" ? "auto" : "100%",
                }}
              />
            </StyledChipWrapper>
            {!disabled && (
              <>
                {loading ? (
                  <SpinnerLoader />
                ) : (
                  <>
                    {showXIcon && (
                      <Trigger
                        onMouseDown={handleMouseDown}
                        onClick={handleDeselect}
                        data-testid="deselect-all"
                        css={`
                          display: flex;
                          justify-content: center;
                        `}
                      >
                        <StyledComboBoxCloseSmallIcon />
                      </Trigger>
                    )}
                    {invalid && errorMessage && (
                      <Tooltip title={errorMessage}>
                        {size === "regular" ? (
                          <StyledInfoSmallIcon />
                        ) : (
                          <StyledInfoIcon />
                        )}
                      </Tooltip>
                    )}
                    {showMenu && (
                      <StyledComboBoxCollapseExpandSingleIcon $open={open} />
                    )}
                  </>
                )}
              </>
            )}
          </StyledInputWrapper>
        </>
      ) : (
        <StyledComboBoxInput
          className={classNames("c-combo-box-input", inputWrapperClassName)}
          onBlur={handleBlur}
          wrapperClick={handleOpen}
          onFocus={() => {
            setFocused(!focused);
            handleOpen();
          }}
          onClick={handleOpen}
          mode={triggerMode}
          wrapRef={comboBoxRef}
          ref={handleRef}
          value={loading && loadingText ? loadingText : value}
          onKeyDown={handleOnKeyDown}
          onChange={handleOnChange}
          placeholder={placeholder}
          disabled={disabled}
          size={size}
          invalid={invalid}
          $loading={loading}
          startAdornment={startAdornment}
          endAdornment={
            !disabled ? (
              <InputAdornment disablePointerEvents={disabled}>
                {loading && <SpinnerLoader />}
                {!loading && selected && !hideClearButton ? (
                  <IconButton
                    onMouseDown={handleMouseDown}
                    onClick={handleDeselect}
                    variant="text gray"
                    size="small"
                  >
                    <StyledComboBoxCloseSmallIcon />
                  </IconButton>
                ) : null}
                {invalid && errorMessage && (
                  <Tooltip title={errorMessage}>
                    {size === "regular" ? (
                      <StyledInfoSmallIcon />
                    ) : (
                      <StyledInfoIcon />
                    )}
                  </Tooltip>
                )}
                {showMenu && (
                  <StyledComboBoxCollapseExpandSingleIcon $open={open} />
                )}
              </InputAdornment>
            ) : null
          }
        />
      )}
      {!disabled ? (
        <ConditionalWrapper
          condition={inPortal}
          wrap={(children) => <Portal>{children}</Portal>}
        >
          <Popper
            anchorEl={comboBoxRef.current}
            open={open}
            placement="bottom"
            style={{
              zIndex: 10,
              width: comboBoxRef.current?.clientWidth,
            }}
            ref={wrapperRef}
            strategy="fixed"
          >
            <RemoveScroll noIsolation allowPinchZoom>
              <StyledComboBoxList
                className="c-combo-box--list"
                $width={comboBoxRef.current?.clientWidth}
                $hide={!showMenu}
              >
                <Autocomplete
                  {...prop}
                  emptyValue={emptyValue}
                  noResultText={noResultText}
                  inputEl={childNode}
                  selected={selected}
                  handleChange={handleChange}
                  renderOption={handleRenderOption}
                  options={options}
                  type={type}
                  defaultValue={defaultValue}
                  handleEmptyAction={emptyAction}
                  disableVirtualization={disableVirtualization}
                />
              </StyledComboBoxList>
            </RemoveScroll>
          </Popper>
        </ConditionalWrapper>
      ) : null}
    </div>
  );
};

ComboBox.displayName = "ComboBox";
